import type { DealerSiteItem, DealerSiteSummary } from '../../models/entities/dealer-api.model';
import type { SiteLocationsResponse } from '../../models/entities/site-location.model';
import type { SiteStats } from '../../models/entities/site-stats.model';

/**
 * When bypass is off, returns a numeric allow-list (empty array = no sites allowed).
 * When bypass is on, or allowed is null/undefined, returns null (no filtering).
 */
export function resolveDealerAllowedSiteIds(
  allowedSiteIds: number[] | null | undefined,
  bypassAllowedSitesFilter: boolean,
): number[] | null {
  if (bypassAllowedSitesFilter || allowedSiteIds == null) {
    return null;
  }
  return allowedSiteIds.map((id) => Number(id));
}

/** Armed/disarmed dashboard counts after optional allowed-sites intersection. */
export function mapDealerSiteStatsCounts(
  armedSites: DealerSiteSummary[] | null | undefined,
  disarmedSites: DealerSiteSummary[] | null | undefined,
  allowedSiteIds: number[] | null | undefined,
  bypassAllowedSitesFilter: boolean,
): SiteStats {
  const allow = resolveDealerAllowedSiteIds(allowedSiteIds, bypassAllowedSitesFilter);
  const isAllowed = (siteId: number): boolean => !allow || allow.includes(siteId);

  const armedCount = (armedSites ?? []).filter((site) => isAllowed(Number(site.siteId))).length;
  const disarmedCount = (disarmedSites ?? []).filter((site) =>
    isAllowed(Number(site.siteId)),
  ).length;

  return { armedCount, disarmedCount };
}

/** Maps Dealer site search rows into the Agent UI site-picker contract. */
export function mapDealerSitesToLocations(
  sites: DealerSiteItem[],
  allowedSiteIds?: number[] | null,
  armedSiteIds?: ReadonlySet<number>,
): SiteLocationsResponse {
  const allow = allowedSiteIds?.map((id) => Number(id)) ?? null;
  const items = sites
    .filter((site) => (allow ? allow.includes(Number(site.siteId)) : true))
    .map((site) => ({
      id: String(site.siteId),
      name: site.siteName,
      address: site.siteAddress?.trim() ?? '',
      armed: armedSiteIds?.has(Number(site.siteId)) ?? false,
    }));

  return { items };
}

/** Builds site picker + armed/disarmed lists from Dealer armed/disarmed summaries. */
export function mapDealerSiteSummariesToLocations(
  armedSites: DealerSiteSummary[] | null | undefined,
  disarmedSites: DealerSiteSummary[] | null | undefined,
  allowedSiteIds?: number[] | null,
): SiteLocationsResponse {
  const allow = allowedSiteIds?.map((id) => Number(id)) ?? null;
  const armedIds = new Set((armedSites ?? []).map((site) => Number(site.siteId)));
  const bySiteId = new Map<number, SiteLocationsResponse['items'][number]>();

  for (const site of [...(armedSites ?? []), ...(disarmedSites ?? [])]) {
    const siteId = Number(site.siteId);
    if (allow && !allow.includes(siteId)) {
      continue;
    }
    if (bySiteId.has(siteId)) {
      continue;
    }

    bySiteId.set(siteId, {
      id: String(siteId),
      name: site.siteName?.trim() ?? `Site ${siteId}`,
      address: site.siteAddress?.trim() ?? '',
      armed: armedIds.has(siteId),
    });
  }

  return {
    items: [...bySiteId.values()].sort((left, right) => left.name.localeCompare(right.name)),
  };
}
